import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Globe, Zap, ArrowRight, Calendar, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import TypewriterText from '../components/animations/TypewriterText';
import MatrixBackground from '../components/animations/MatrixBackground.tsx';
import ScanLines from '../components/animations/ScanLines';
import GlowingButton from '../components/ui/GlowingButton';
import BackButton from '../components/ui/BackButton';
import DemoCallModal from '../components/modals/DemoCallModal';
import { useSeo } from '../hooks/useSeo';

const solutions = [
  {
    id: 'leads',
    icon: Phone,
    title: "I need high-volume outreach without burning leads",
    description: "We've built AI callers, DM workflows, and outbound systems that hit fast, hit clean, and don't scuff the lead.",
    subtext: "No cringe scripts. Just booked meetings and inboxes full of replies.",
    features: [
      "Voice AI agents for cold calls & follow-ups",
      "Automated SMS & email outreach at scale",
      "Human-like flows that protect lead quality"
    ],
  },
  {
    id: 'website',
    icon: Globe,
    title: "My site was last updated in 2019..",
    description: "If your site looks good but doesn't convert, it's a liability.",
    subtext: "We build landing pages and funnels that turn cold traffic into deals",
    features: [
      "High-converting websites built for traffic",
      "Clear offers, strong CTAs, frictionless layout",
      "Mobile-first, lightning fast"
    ],
    extraButton: {
      text: "See Completed Sites",
      link: "/websites"
    }
  },
  {
    id: 'automation',
    icon: Zap,
    title: "I am juggling too many roles",
    description: "Manual follow-ups, planning and organization...Handled.",
    subtext: "We automate the grunt work so you stay focused on growth.",
    features: [
      "Smart lead tagging + CRM updates",
      "Auto-responders, DMs, and reminders",
      "Calendar sync, notifications, and task flows"
    ],
  }
];

export default function WhatDoIDoPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useSeo({
    title: 'AI Automation & Websites | Point Break Systems',
    description: 'Discover how Point Break Systems builds AI systems that save time, make money, and work 24/7. Solutions for high-volume outreach, converting websites, and business automation.',
    path: '/what-do-i-do',
    schemas: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "What We Do",
        "description": "AI automation and web solutions that save time and make money",
        "url": "https://pointbreak.systems/what-do-i-do",
        "mainEntity": {
          "@type": "Organization",
          "name": "Point Break Systems",
          "service": [
            {
              "@type": "Service",
              "name": "AI Voice Callers",
              "description": "High-volume outreach with AI-powered calling systems"
            },
            {
              "@type": "Service",
              "name": "High-Converting Websites",
              "description": "Web design and development focused on conversion optimization"
            },
            {
              "@type": "Service",
              "name": "Business Automation",
              "description": "Automated workflows and task management systems"
            }
          ]
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Point Break Systems Services",
        "itemListElement": solutions.map((solution, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Service",
            "name": solution.title,
            "description": solution.description,
            "provider": {
              "@type": "Organization",
              "name": "Point Break Systems"
            },
            "serviceType": solution.id === 'leads' ? 'AI Voice Calling' :
                          solution.id === 'website' ? 'Web Development' :
                          'Business Automation'
          }
        }))
      },
    ],
  });

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <div className="absolute inset-0">
        <MatrixBackground />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(180deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:24px_24px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-transparent" />
      </div>

      <div className="relative z-10 py-8 sm:py-16 lg:py-24">
        <BackButton />
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6 sm:mb-16">
            <h1 className="tech-title text-4xl md:text-5xl text-[#eaeaea] mb-6">
              <TypewriterText text="Let's Keep It Simple." delay={50} />
            </h1>
            <p className="text-xl font-bold text-cyan-400/90">
              <TypewriterText 
                text="We build AI systems that save time, make money, and don't sleep."
                delay={30}
              />
            </p>
            <p className="text-lg font-bold text-[#eaeaea]/80 mt-4">
              <TypewriterText 
                text="Pick the solution that fits your business:"
                delay={30}
              />
            </p>
          </div>

          <div className="space-y-8">
            {solutions.map((solution) => (
              <motion.div
                key={solution.id}
                className="relative group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                whileHover={{ scale: 1.02 }}
              >
                <button
                  onClick={() => setExpandedId(expandedId === solution.id ? null : solution.id)}
                  className="w-full text-left"
                >
                  <div className="relative overflow-hidden">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-[#FFD700] rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300" />
                    <div className="relative bg-[#0a0a0a] p-4 sm:p-6 rounded-xl border border-cyan-500/20 group-hover:border-cyan-500/40 transition-all duration-300">
                      <div className="flex items-start space-x-3 sm:space-x-6">
                        <div className="flex-shrink-0 p-3 bg-black/50 rounded-lg">
                          <solution.icon className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div>
                          <h2 className="text-[#eaeaea] text-lg font-tech font-bold mb-2" id={`solution-${solution.id}`}>{solution.title}</h2>
                          <p className="text-[#eaeaea]/70">{solution.description}</p>
                        </div>
                      </div>

                      <AnimatePresence>
                        {expandedId === solution.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-6 pt-6 border-t border-cyan-500/10"
                          >
                            <p className="text-[#eaeaea]/80 mb-4">{solution.subtext}</p>
                            
                            <div className="mb-6">
                              <h3 className="text-cyan-400 font-tech mb-3" id={`solution-${solution.id}-features`}>How we solve it:</h3>
                              <ul className="space-y-2">
                                {solution.features.map((feature, index) => (
                                  <li key={index} className="flex items-center text-[#eaeaea]/80">
                                    <span className="text-[#FFD700] mr-2">›</span>
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4">
                              <a
                                href="https://calendly.com/noahblanchard/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="sm:w-1/3"
                              >
                                <GlowingButton variant="primary" className="w-full group py-2">
                                  <span className="flex items-center justify-center text-[#FFD700] text-sm">
                                    <Calendar className="w-4 h-4 mr-1" />
                                    Book a Call
                                  </span>
                                </GlowingButton>
                              </a>
                              {solution.extraButton ? (
                                <Link to={solution.extraButton.link} className="sm:w-2/3">
                                  <GlowingButton variant="secondary" className="w-full group py-2">
                                    <span className="flex items-center justify-center text-cyan-400 text-sm">
                                      <Globe className="w-4 h-4 mr-1" />
                                      {solution.extraButton.text}
                                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </span>
                                  </GlowingButton>
                                </Link>
                              ) : (
                                <GlowingButton 
                                  variant="secondary" 
                                  className="sm:w-2/3 w-full group py-2"
                                  onClick={() => setIsModalOpen(true)}
                                >
                                  <span className="flex items-center justify-center text-cyan-400 text-sm">
                                    <Phone className="w-4 h-4 mr-1" />
                                    Get a Demo Call
                                    <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                  </span>
                                </GlowingButton>
                              )}
                            </div>
                            <p className="text-[#FFD700]/60 text-sm mt-4 text-center">
                              Limited availability: 2 build slots remaining this month
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="relative group mt-12"
            >
              <div className="relative overflow-hidden">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#FFD700] to-amber-500 rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300" />
                <div className="relative bg-[#0a0a0a] p-4 sm:p-6 rounded-xl border border-[#FFD700]/20 group-hover:border-[#FFD700]/40 transition-all duration-300">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 p-3 bg-black/50 rounded-lg">
                        <Sparkles className="w-6 h-6 text-[#FFD700]" />
                      </div>
                      <div>
                        <h2 className="text-[#FFD700] text-lg font-tech" id="special-request">Have a special request?</h2>
                        <p className="text-[#eaeaea]/70 mt-1">If it saves time or makes money, we're in.</p>
                      </div>
                    </div>
                    <Link to="/contact">
                      <GlowingButton variant="primary" className="group">
                        <span className="flex items-center text-[#FFD700]">
                          Let's Talk
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </GlowingButton>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <ScanLines />
      
      <DemoCallModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}

